import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Clock,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { adminStats } from "../../lib/api";

const statusMeta = {
  neu: { label: "Neu", color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
  in_bearbeitung: {
    label: "In Bearbeitung",
    color: "text-amber-400",
    bg: "bg-amber-500/10 border-amber-500/30",
  },
  beantwortet: {
    label: "Beantwortet",
    color: "text-blue-400",
    bg: "bg-blue-500/10 border-blue-500/30",
  },
  abgeschlossen: {
    label: "Abgeschlossen",
    color: "text-green-400",
    bg: "bg-green-500/10 border-green-500/30",
  },
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-400">
        {label}
      </div>
      <Icon size={16} className={accent || "text-neutral-400"} />
    </div>
    <div className="mt-4 font-display font-extrabold text-4xl text-white">
      {value}
    </div>
  </div>
);

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const d = await adminStats();
      setData(d);
    } catch (e) {
      setErr("Statistiken konnten nicht geladen werden.");
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (err)
    return (
      <div className="p-6 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300">
        <AlertCircle className="inline mr-2" size={16} />
        {err}
      </div>
    );
  if (!data)
    return <div className="text-neutral-400">Laden…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-pink-400">
            Übersicht
          </div>
          <h1 className="mt-1 font-display font-extrabold text-3xl md:text-5xl tracking-tight">
            Dashboard
          </h1>
        </div>
        <Link
          to="/admin/leads"
          className="hidden sm:inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-neutral-950 text-sm font-bold uppercase tracking-wider hover:bg-gradient-to-r hover:from-pink-400 hover:via-orange-400 hover:to-purple-400 hover:text-white transition-all"
        >
          Alle Anfragen <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Calendar} label="Heute" value={data.today} accent="text-pink-400" />
        <StatCard icon={TrendingUp} label="Diese Woche" value={data.week} accent="text-orange-400" />
        <StatCard icon={Inbox} label="Gesamt" value={data.total} accent="text-purple-400" />
      </div>

      <div className="mt-10 grid lg:grid-cols-4 gap-4">
        {Object.entries(data.by_status).map(([key, count]) => {
          const meta = statusMeta[key] || { label: key, color: "text-white", bg: "bg-white/5" };
          return (
            <div
              key={key}
              className={`p-5 rounded-2xl border ${meta.bg}`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">
                Status
              </div>
              <div className={`mt-1 text-sm font-bold ${meta.color}`}>{meta.label}</div>
              <div className="mt-4 font-display font-extrabold text-3xl text-white">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-12">
        <h2 className="font-display font-extrabold text-xl tracking-tight mb-4">
          Letzte 5 Anfragen
        </h2>
        <div className="rounded-2xl bg-white/5 border border-white/10 divide-y divide-white/5">
          {data.recent.length === 0 && (
            <div className="p-6 text-sm text-neutral-400">Noch keine Anfragen vorhanden.</div>
          )}
          {data.recent.map((lead) => {
            const meta = statusMeta[lead.status] || statusMeta.neu;
            return (
              <Link
                key={lead.id}
                to="/admin/leads"
                className="flex items-center justify-between gap-4 p-4 md:p-5 hover:bg-white/5 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-white truncate">{lead.name}</div>
                    <span className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${meta.bg} ${meta.color}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-400 truncate">
                    {lead.email} {lead.leistung ? `· ${lead.leistung}` : ""}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-neutral-500 flex items-center gap-2">
                  <Clock size={12} />
                  {new Date(lead.created_at).toLocaleString("de-DE")}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
